import './index.scss'
import './i18n'
import './i18n'

import {
  AuthenticationResult,
  BrowserCacheLocation,
  EventMessage,
  EventType,
  PublicClientApplication
} from '@azure/msal-browser'
import config from 'components/providers/outlook/config'
import dayjs from 'dayjs'
import dayOfYear from 'dayjs/plugin/dayOfYear'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import ReactDOM from 'react-dom/client'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { isCypress } from 'utils/cypress'
import { isTrulyEmpty } from 'utils/object'

import App from './App'
import reportWebVitals from './reportWebVitals'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import { isTestEnv } from 'utils/env'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(localizedFormat) // use the plugin
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(dayOfYear)
dayjs.extend(isBetween)

export function getMidnightUTC(dateString: string): number {
  // Parse the given date string and set the time to midnight in UTC
  const utcMidnight = dayjs(dateString).utc().startOf('day')

  // Return the timestamp in seconds since the epoch
  return utcMidnight.unix()
}
// Set the global timezone
let globalTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
dayjs.tz.setDefault(globalTimezone)

// Function to set the default timezone
export const setDefaultTimezone = (timezone: string) => {
  globalTimezone = timezone
  dayjs.tz.setDefault(globalTimezone)
  LocalStorage.set(LocalStorageKey.TIMEZONE, timezone)
}

let cachedTimezone: string | null = null

export const getDefaultTimezone = (): string => {
  if (cachedTimezone && !isCypress()) {
    if (cachedTimezone === 'Etc/Unknown' || cachedTimezone.length === 3) {
      cachedTimezone = dayjs.tz.guess()
    }

    return cachedTimezone
  }

  const defaultTimezone =
    LocalStorage.get(LocalStorageKey.TIMEZONE) || globalTimezone

  try {
    cachedTimezone = defaultTimezone
  } catch (error) {
    cachedTimezone = dayjs.tz.guess()
  }

  if (cachedTimezone === 'Etc/Unknown' || cachedTimezone.length === 3) {
    cachedTimezone = dayjs.tz.guess()
  }

  return cachedTimezone
}

// Updated MSAL Instance configuration
export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: config.appId,
    redirectUri: config.redirectUri
    // Add authority if needed (e.g., for multi-tenant apps)
    // authority: "https://login.microsoftonline.com/common"
  },
  cache: {
    cacheLocation: BrowserCacheLocation.SessionStorage, // Use enum instead of string
    storeAuthStateInCookie: true
  },
  system: {
    // Optional: Configure logger
    allowRedirectInIframe: true,
    loggerOptions: {
      logLevel: 3, // Error = 0, Warning = 1, Info = 2, Verbose = 3
      piiLoggingEnabled: false
    }
  }
})

// Check if there are already accounts in the browser session
// If so, set the first account as the active account
const accounts = msalInstance.getAllAccounts()
if (accounts && accounts.length > 0) {
  msalInstance.setActiveAccount(accounts[0])
}

msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    // Set the active account - this simplifies token acquisition
    const authResult = event.payload as AuthenticationResult
    msalInstance.setActiveAccount(authResult.account)
  }
})

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

if (isTestEnv() && isTrulyEmpty(LocalStorage.get(LocalStorageKey.PASSWORD))) {
  root.render(<div>{'no auth'}</div>)
} else {
  root.render(<App pca={msalInstance} />)
}

//init pwa
serviceWorkerRegistration.register({
  onUpdate(registration) {
    // We trigger update mostly because it is very unlikely that the user will close the app anytime soon
    // https://developer.chrome.com/docs/workbox/service-worker-lifecycle/
    // Wrap in try-catch and add delay to avoid immediate update failures on mobile
    setTimeout(() => {
      registration.update().catch((err) => {
        console.warn(
          'Service worker update failed, will retry on next visit:',
          err.message
        )
      })
    }, 1000)
  }
})

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
